import { useState, useEffect } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useFlightRestrictionZonesQuery } from "@/api/v0/flight-restriction-zones";
import { findZoneBySlug } from "@/lib/zone-utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  AlertTriangle, 
  MapPin, 
  Clock, 
  Search,
  RefreshCw,
  Download,
  Filter,
  ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";

interface ZoneViolation {
  id: string;
  zoneId: number;
  detectionId: string;
  sensorId: string;
  droneIdentifier: string;
  violationTime: string;
  latitude: number;
  longitude: number;
  processedAt: string;
  alertSent: boolean;
  alertSentAt?: string;
}

export const FlightRestrictionZoneViolationsRoute = () => {
  const { zoneSlug } = useParams({ from: "/flight-restriction-zones/$zoneSlug/violations" });
  const [violations, setViolations] = useState<ZoneViolation[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 days ago
    to: new Date().toISOString().split('T')[0] // today
  });

  const { 
    data: zonesData, 
    isLoading: zoneLoading, 
    error: zoneError 
  } = useFlightRestrictionZonesQuery();

  // Find the zone from the paginated response
  const foundZone = zonesData?.zones ? findZoneBySlug(zonesData.zones, zoneSlug) : null;
  const zone = foundZone ? {
    id: foundZone.frz_id || 0,
    name: foundZone.name || '',
    type: foundZone.type || 'Unknown',
    source: foundZone.source || '',
    version: foundZone.version || '',
    effective_from: foundZone.effective_from || '',
    effective_to: foundZone.effective_to || null,
    active: foundZone.active || false,
    last_updated: foundZone.last_updated || '',
    h3_indexes: foundZone.h3_indexes || [],
    data: foundZone.data || {},
    geometry: foundZone.geometry || '',
  } : null;

  // Handle case when zone is not found
  useEffect(() => {
    if (!zoneLoading && !zone && zonesData?.zones) {
      // Zone not found, redirect to main page
      window.location.href = '/flight-restriction-zones';
    }
  }, [zoneLoading, zone, zonesData]);

  // Mock data for demonstration - in real implementation, this would come from API
  const mockViolations: ZoneViolation[] = [
    {
      id: "1",
      zoneId: zone?.id || 0,
      detectionId: "det-001",
      sensorId: "sensor-001",
      droneIdentifier: "DRONE-ABC123",
      violationTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      latitude: 51.5074,
      longitude: -0.1278,
      processedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
      alertSent: true,
      alertSentAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: "2",
      zoneId: zone?.id || 0,
      detectionId: "det-002",
      sensorId: "sensor-002",
      droneIdentifier: "DRONE-XYZ789",
      violationTime: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      latitude: 51.5084,
      longitude: -0.1288,
      processedAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      alertSent: false,
    },
    {
      id: "3",
      zoneId: zone?.id || 0,
      detectionId: "det-003",
      sensorId: "sensor-001",
      droneIdentifier: "DRONE-DEF456",
      violationTime: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      latitude: 51.5064,
      longitude: -0.1268,
      processedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      alertSent: true,
      alertSentAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    },
  ];

  useEffect(() => {
    if (zone && !zoneLoading) {
      fetchViolations();
    }
  }, [zone, zoneLoading, dateRange]);

  const fetchViolations = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setViolations(mockViolations);
    } catch (error) {
      toast.error("Failed to fetch violations");
    } finally {
      setLoading(false);
    }
  };

  const filteredViolations = violations.filter(violation =>
    violation.droneIdentifier.toLowerCase().includes(searchTerm.toLowerCase()) ||
    violation.detectionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    violation.sensorId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRefresh = () => {
    fetchViolations();
  };

  const handleExport = () => {
    // In real implementation, this would export the violations data
    toast.success("Violations exported successfully");
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (zoneLoading) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (zoneError || !zone) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/flight-restriction-zones">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Zones
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
              <h3 className="text-lg font-semibold mb-2">Zone not found</h3>
              <p className="text-muted-foreground mb-4">
                The flight restriction zone you're looking for doesn't exist or has been removed.
              </p>
              <Button asChild>
                <Link to="/flight-restriction-zones">View All Zones</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const breadcrumbs = [
    { label: "Flight Restrictions", href: "/flight-restriction-zones" },
    { label: zone?.name || "Zone", href: `/flight-restriction-zones/${zoneSlug}` },
    { label: "Violations", href: `/flight-restriction-zones/${zoneSlug}/violations` },
  ];

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav items={breadcrumbs} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/flight-restriction-zones/${zoneSlug}` as any}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Zone
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-destructive" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Violations for {zone?.name || "Zone"}</h1>
              <p className="text-muted-foreground">
                View and manage violations for this flight restriction zone
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="destructive">
            {violations.length} violations
          </Badge>
        </div>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters & Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-2 flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search violations..."
                  className="pl-8 w-64"
                />
              </div>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="w-40"
                />
                <Input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="w-40"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Violations Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Violations</CardTitle>
          <CardDescription>
            {filteredViolations.length} of {violations.length} violations shown
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Drone ID</TableHead>
                  <TableHead>Detection ID</TableHead>
                  <TableHead>Sensor ID</TableHead>
                  <TableHead>Violation Time</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Alert Sent</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredViolations.length > 0 ? (
                  filteredViolations.map((violation) => (
                    <TableRow key={violation.id}>
                      <TableCell className="font-medium">
                        {violation.droneIdentifier}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {violation.detectionId}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {violation.sensorId}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="h-3 w-3" />
                          {formatDateTime(violation.violationTime)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin className="h-3 w-3" />
                          {violation.latitude.toFixed(4)}, {violation.longitude.toFixed(4)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={violation.alertSent ? "default" : "secondary"}>
                          {violation.alertSent ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <AlertTriangle className="h-8 w-8 text-muted-foreground" />
                        <p className="text-muted-foreground">No violations found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Violations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{violations.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Alerts Sent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {violations.filter(v => v.alertSent).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Unique Drones</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(violations.map(v => v.droneIdentifier)).size}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Unique Sensors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(violations.map(v => v.sensorId)).size}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
